import React, { useEffect, useState, useRef } from 'react';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  window.location.hostname === 'localhost' ? 'http://localhost:8000' : ''
);
const VENMO_HANDLE = '@damnjj-wtf';
const VENMO_URL = process.env.REACT_APP_VENMO_URL || 'https://venmo.com/u/damnjj-wtf';
const VENMO_APP_URL = process.env.REACT_APP_VENMO_APP_URL || 'venmo://users/damnjj-wtf';
const PATREON_URL = process.env.REACT_APP_PATREON_URL || 'https://www.patreon.com/16002907/join';
const SUPPORT_URL = process.env.REACT_APP_SUPPORT_URL || VENMO_URL;
const SUPPORT_DISPLAY_URL = VENMO_HANDLE;

const STATIONS = [
  {
    id: "wers",
    name: "WERS 88.9FM",
    school: "Emerson College",
    location: "Boston, MA",
    streamUrl: "https://playerservices.streamtheworld.com/api/livestream-redirect/WERSFM.mp3",
    method: "audio"
  },
  {
    id: "wfuv",
    name: "WFUV 90.7FM",
    school: "Fordham University",
    location: "Bronx, NY",
    streamUrl: "https://onair.wfuv.org/onair-hi",
    method: "audio"
  },
  {
    id: "kalx",
    name: "KALX 90.7FM",
    school: "UC Berkeley",
    location: "Berkeley, CA",
    streamUrl: "https://stream.kalx.berkeley.edu:8443/kalx-128.mp3",
    method: "audio"
  },
  {
    id: "wicb",
    name: "WICB 91.7FM",
    school: "Ithaca College",
    location: "Ithaca, NY",
    streamUrl: "https://icecast.do.zufall.co/wicb_mp3_high",
    method: "video"
  },
  {
    id: "wrsu",
    name: "WRSU 88.7FM",
    school: "Rutgers University",
    location: "New Brunswick, NJ",
    streamUrl: "https://wrsu-libstrm.radioca.st/stream",
    method: "video"
  },
  {
    id: "whpk",
    name: "WHPK 88.5FM",
    school: "University of Chicago",
    location: "Chicago, IL",
    streamUrl: "https://whpk-stream.uchicago.edu/stream",
    method: "whpk-direct"
  }
];

function BeatMeter({ active }) {
  const idleLevels = [2, 3, 2, 3, 2, 3];
  const [levels, setLevels] = useState(idleLevels);

  useEffect(() => {
    if (!active) {
      setLevels(idleLevels);
      return undefined;
    }

    const interval = window.setInterval(() => {
      setLevels((currentLevels) => (
        currentLevels.map((level, index) => {
          const chainLift = Math.sin((Date.now() / 180) + index * 0.9) > 0.35 ? 2 : 0;
          const jitter = Math.floor(Math.random() * 5) - 2;
          const nextLevel = level + jitter + chainLift - 1;
          return Math.max(2, Math.min(9, nextLevel));
        })
      ));
    }, 170);

    return () => window.clearInterval(interval);
  }, [active]);

  return (
    <div className={`stack-eq ${active ? 'is-active' : ''}`} aria-hidden="true">
      {levels.map((height, barIndex) => (
        <div
          key={`eq-bar-${barIndex}`}
          className="eq-bar"
          style={{
            '--eq-delay': `${barIndex * 140}ms`
          }}
        >
          {Array.from({ length: 9 }).map((_, segmentIndex) => (
            <span
              key={`eq-segment-${barIndex}-${segmentIndex}`}
              className={segmentIndex < height ? 'is-lit' : ''}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function getStationStreamUrl(station) {
  if (station.proxyPath) {
    return `${BACKEND_URL}${station.proxyPath}`;
  }
  return station.streamUrl;
}

function App() {
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStationId, setLoadingStationId] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null); // Add video ref for KALX
  const isStoppingRef = useRef(false);
  const currentStationRef = useRef(null);

  const openVenmo = (event) => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);

    if (!isMobile) {
      return;
    }

    event.preventDefault();
    const fallbackTimeout = window.setTimeout(() => {
      window.location.href = VENMO_URL;
    }, 900);

    const cancelFallback = () => window.clearTimeout(fallbackTimeout);
    window.addEventListener('pagehide', cancelFallback, { once: true });
    window.addEventListener('blur', cancelFallback, { once: true });
    window.location.href = VENMO_APP_URL;
  };

  const playStation = async (station) => {
    try {
      // If same station is playing, stop it
      if (currentStation?.id === station.id && isPlaying) {
        stopStation();
        return;
      }

      isStoppingRef.current = false;
      setError(null); // Clear any previous errors first
      setIsLoading(true);
      setLoadingStationId(station.id);
      currentStationRef.current = station;

      // Stop any currently playing station
      if (audioRef.current && isPlaying) {
        audioRef.current.pause();
      }
      if (videoRef.current && isPlaying) {
        videoRef.current.pause();
      }

      const streamUrl = getStationStreamUrl(station);

      if (station.method === 'whpk-direct') {
        audioRef.current = new Audio(streamUrl);

        audioRef.current.addEventListener('canplay', () => {
          console.log('WHPK ready to play');
          setIsLoading(false);
          setLoadingStationId(null);
        });

        audioRef.current.addEventListener('error', (e) => {
          if (isStoppingRef.current || !currentStationRef.current) return;
          const activeStation = currentStationRef.current;
          console.error('WHPK audio error:', e);
          const errorMsg = e.target.error ? `Error ${e.target.error.code}` : 'Playback failed';
          setError(`${activeStation.name} failed to play: ${errorMsg}`);
          setIsLoading(false);
          setLoadingStationId(null);
          setIsPlaying(false);
        });

        audioRef.current.addEventListener('ended', () => {
          console.log('WHPK audio ended');
          stopStation();
        });

        console.log('Playing', station.name, 'using WHPK site pattern from:', streamUrl);

        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          setCurrentStation(station);
          setIsPlaying(true);
          setIsLoading(false);
          setLoadingStationId(null);
          console.log('Successfully playing WHPK direct:', station.name);
        }
      } else if (station.method === 'video') {
        if (!videoRef.current) {
          videoRef.current = document.createElement('video');
          videoRef.current.playsInline = true;
          videoRef.current.crossOrigin = 'anonymous';
          videoRef.current.controls = false;
          videoRef.current.style.display = 'none';
          document.body.appendChild(videoRef.current);

          videoRef.current.addEventListener('canplay', () => {
            console.log('Video ready to play');
            setIsLoading(false);
            setLoadingStationId(null);
          });

          videoRef.current.addEventListener('error', () => {
            if (isStoppingRef.current || !currentStationRef.current) return;
            const activeStation = currentStationRef.current;
            const mediaError = videoRef.current.error;
            let errorMsg = 'Playback failed';
            if (mediaError) {
              switch (mediaError.code) {
                case mediaError.MEDIA_ERR_NETWORK:
                  errorMsg = 'Network error';
                  break;
                case mediaError.MEDIA_ERR_DECODE:
                  errorMsg = 'Decode error';
                  break;
                case mediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                  errorMsg = 'Format not supported';
                  break;
                default:
                  errorMsg = 'Unknown error';
              }
            }
            setError(`${activeStation.name} failed to play: ${errorMsg}`);
            setIsLoading(false);
            setLoadingStationId(null);
            setIsPlaying(false);
          });

          videoRef.current.addEventListener('ended', () => {
            console.log('Video ended');
            stopStation();
          });
        }

        console.log('Playing', station.name, 'via video from:', streamUrl);
        videoRef.current.src = streamUrl;
        videoRef.current.load();

        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          setCurrentStation(station);
          setIsPlaying(true);
          setIsLoading(false);
          setLoadingStationId(null);
          console.log('Successfully playing via video:', station.name);
        }
      } else {
        if (!audioRef.current) {
          audioRef.current = new Audio();

          audioRef.current.addEventListener('loadstart', () => {
            console.log('Audio loading started');
          });

          audioRef.current.addEventListener('canplay', () => {
            console.log('Audio ready to play');
            setIsLoading(false);
            setLoadingStationId(null);
          });

          audioRef.current.addEventListener('error', (e) => {
            if (isStoppingRef.current || !currentStationRef.current) return;
            const activeStation = currentStationRef.current;
            console.error('Audio error for', activeStation.name, ':', e);
            const errorMsg = e.target.error ? `Error ${e.target.error.code}` : 'Playback failed';
            setError(`${activeStation.name} failed to play: ${errorMsg}`);
            setIsLoading(false);
            setLoadingStationId(null);
            setIsPlaying(false);
          });

          audioRef.current.addEventListener('ended', () => {
            console.log('Audio ended');
            stopStation();
          });
        }

        console.log('Playing', station.name, 'via audio from:', streamUrl);

        audioRef.current.src = streamUrl;
        audioRef.current.load();

        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          setCurrentStation(station);
          setIsPlaying(true);
          setIsLoading(false);
          setLoadingStationId(null);
          console.log('Successfully playing via audio:', station.name);
        }
      }

    } catch (e) {
      if (isStoppingRef.current || !currentStationRef.current) {
        setIsLoading(false);
        setLoadingStationId(null);
        return;
      }

      console.error('Play error:', e);
      let errorMessage = `Failed to play ${station.name}`;

      if (e.name === 'NotAllowedError') {
        errorMessage += ': User interaction required';
      } else if (e.name === 'NotSupportedError') {
        errorMessage += ': Format not supported';
      } else if (e.message) {
        errorMessage += `: ${e.message}`;
      }

      setError(errorMessage);
      setIsLoading(false);
      setLoadingStationId(null);
      setIsPlaying(false);
    }
  };

  const stopStation = () => {
    isStoppingRef.current = true;
    if (audioRef.current) {
      console.log('Stopping audio playback');
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    if (videoRef.current) {
      console.log('Stopping video playback');
      videoRef.current.pause();
      videoRef.current.src = '';
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    setCurrentStation(null);
    currentStationRef.current = null;
    setIsPlaying(false);
    setIsLoading(false);
    setLoadingStationId(null);
    setError(null); // Clear any error messages when stopping
    setTimeout(() => {
      isStoppingRef.current = false;
    }, 1500);
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '20px',
      fontFamily: 'ui-monospace, Monaco, "Cascadia Code", "Segoe UI Mono", "Roboto Mono", "Oxygen Mono", "Ubuntu Monospace", "Source Code Pro", "Fira Code", "Droid Sans Mono", "Courier New", monospace',
      backgroundColor: '#0a0a0a',
      color: '#ffffff'
    }}>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes eqJump {
          0%, 100% {
            transform: translateY(0);
            filter: brightness(0.86);
          }
          45% {
            transform: translateY(-2px);
            filter: brightness(1.22);
          }
        }

        .station-grid {
          width: min(100%, 780px);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          align-items: stretch;
        }

        .station-card {
          min-height: 158px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .top-actions {
          width: min(100%, 780px);
          margin: 0 auto;
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .support-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          padding: 8px 14px;
          border: 1px solid #343434;
          border-radius: 999px;
          background: #111;
          color: #f5f5f5;
          text-decoration: none;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 0.88rem;
          font-weight: 700;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
          transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
          outline: none;
          -webkit-tap-highlight-color: transparent;
        }

        .support-pill:hover {
          border-color: #4ecdc4;
          color: #4ecdc4;
          transform: translateY(-1px);
        }

        .support-pill:focus,
        .support-pill:focus-visible {
          outline: none;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
          border-color: #343434;
        }

        .support-pill.secondary {
          background: #151515;
          color: #d8d8d8;
        }

        .label-title {
          margin: 0 0 0.5rem;
        }

        .label-title-image {
          display: block;
          width: min(92vw, 760px);
          height: auto;
          margin: 0 auto;
          filter: drop-shadow(0 18px 28px rgba(0,0,0,0.48));
        }

        .hero-copy {
          max-width: 680px;
          margin: 0 auto 2.5rem;
          color: #a8b0bd;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 1rem;
          line-height: 1.65;
        }

        .section-heading {
          width: min(100%, 780px);
          margin: 0 auto 12px;
        }

        .section-heading h2 {
          color: #f5f5f5;
          font-size: 1.1rem;
          margin: 0 0 4px;
          font-weight: 700;
        }

        .section-heading p {
          color: #9ca3af;
          font-size: 0.9rem;
          margin: 0;
          line-height: 1.5;
        }

        .retro-player {
          width: min(100%, 780px);
          margin: 0 auto 18px;
          padding: 14px 16px;
          border: 1px solid #343434;
          border-radius: 6px;
          background: linear-gradient(180deg, #202020 0%, #111 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), 0 18px 40px rgba(0,0,0,0.28);
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 16px;
          align-items: center;
        }

        .player-eyebrow {
          color: #858585;
          font-size: 0.72rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 4px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .on-air-light {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #4a4a4a;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.08);
        }

        .on-air-light.is-live {
          background: #ff3131;
          box-shadow: 0 0 10px rgba(255,49,49,0.85), 0 0 18px rgba(255,49,49,0.45);
          animation: pulse 1.6s ease-in-out infinite;
        }

        .player-title {
          color: #f5f5f5;
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 2px;
        }

        .player-meta {
          color: #9ca3af;
          font-size: 0.82rem;
        }

        .stack-eq {
          width: 154px;
          height: 58px;
          padding: 8px 10px;
          border: 1px solid #2d2d2d;
          border-radius: 4px;
          background: linear-gradient(180deg, #111 0%, #070707 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 7px;
          align-items: end;
        }

        .eq-bar {
          display: flex;
          flex-direction: column-reverse;
          gap: 3px;
          height: 100%;
          animation-delay: var(--eq-delay);
        }

        .eq-bar span {
          height: 3px;
          border-radius: 2px;
          background: #232323;
          opacity: 0.62;
          transition: background-color 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
        }

        .eq-bar span.is-lit {
          background: #3ba85d;
          opacity: 0.95;
          box-shadow: 0 0 6px rgba(59,168,93,0.26);
        }

        .eq-bar span.is-lit:nth-child(n + 5) {
          background: #b5913c;
          box-shadow: 0 0 6px rgba(181,145,60,0.26);
        }

        .eq-bar span.is-lit:nth-child(n + 8) {
          background: #b4483d;
          box-shadow: 0 0 6px rgba(180,72,61,0.3);
        }

        .stack-eq.is-active .eq-bar {
          animation: eqJump 560ms ease-in-out infinite;
        }

        @media (max-width: 720px) {
          .top-actions {
            justify-content: center;
          }

          .station-grid {
            grid-template-columns: 1fr;
          }

          .retro-player {
            grid-template-columns: 1fr;
          }

          .stack-eq {
            width: 100%;
          }
        }

        .support-link {
          color: #4ecdc4;
          text-decoration: none;
          font-weight: 700;
        }

        .support-link:hover {
          color: #79ddd8;
          text-decoration: underline;
        }

        .footer-line {
          max-width: 340px;
          margin-left: auto;
          margin-right: auto;
          text-wrap: balance;
        }

        .footer-credit {
          max-width: 340px;
          margin-left: auto;
          margin-right: auto;
          text-wrap: balance;
        }

        .support-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 1rem;
        }

        .support-copy {
          max-width: 320px;
          text-wrap: balance;
        }

      `}</style>

      <div className="top-actions" aria-label="Support">
        <a
          className="support-pill"
          href={VENMO_URL}
          onClick={openVenmo}
          target="_blank"
          rel="noopener noreferrer"
        >
          Venmo
        </a>
        <a
          className="support-pill secondary"
          href={PATREON_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Patreon
        </a>
      </div>

      {/* Header Section */}
      <div className="text-center mt-12 mb-10">
        <h1 className="label-title">
          <img
            className="label-title-image"
            src="/title-label.png"
            alt="collegerad.io"
          />
        </h1>
        <h2 style={{
          fontSize: 'clamp(1.125rem, 4vw, 1.25rem)',
          color: '#9ca3af',
          fontWeight: 'normal',
          marginBottom: '1rem',
          marginTop: '1rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          Live college radio streams from independent campus stations.
        </h2>
        <p className="hero-copy">
          Tune into WERS in Boston, WFUV in the Bronx, KALX in Berkeley, WICB in Ithaca,
          WRSU in New Brunswick, and WHPK in Chicago from one minimal web player.
        </p>
      </div>

      {/* Main Content */}
      <section className="retro-player" aria-label="Current player">
        <div>
          <div className="player-eyebrow">
            <span className={`on-air-light ${isPlaying ? 'is-live' : ''}`} aria-hidden="true" />
            {isPlaying ? 'On Air' : isLoading ? 'Tuning' : 'Standby'}
          </div>
          <div className="player-title">
            {currentStation?.name || (loadingStationId ? STATIONS.find(station => station.id === loadingStationId)?.name : 'No station selected')}
          </div>
          <div className="player-meta">
            {currentStation ? `${currentStation.school} / ${currentStation.location}` : 'Pick a station to start the signal.'}
          </div>
        </div>
        <BeatMeter active={isPlaying || isLoading} />
      </section>

      <section className="section-heading" aria-labelledby="station-list-heading">
        <h2 id="station-list-heading">Campus Stations On Air</h2>
        <p>Independent college radio streams from university stations across the United States.</p>
      </section>

      <main className="station-grid">
        {STATIONS.map(station => (
          <div key={station.id} className="station-card" style={{
            border: '1px solid #333',
            padding: '16px',
            borderRadius: '6px',
            backgroundColor: isLoading && loadingStationId === station.id ? '#2a2a1a' : '#1a1a1a',
            transition: 'background-color 0.3s ease'
          }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: '600' }}>{station.name}</h3>
              <p style={{ margin: '0 0 2px 0', fontSize: '0.9rem', color: '#ccc' }}>{station.school}</p>
              <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#999' }}>{station.location}</p>

              {isLoading && loadingStationId === station.id && (
                <div style={{
                  color: '#ffd700',
                  fontSize: '13px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid #333',
                    borderTop: '2px solid #ffd700',
                    borderRadius: '50%',
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Connecting to {station.name}...
                </div>
              )}
            </div>

            <div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => playStation(station)}
                  disabled={isLoading && loadingStationId === station.id}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: (currentStation?.id === station.id && isPlaying) ? '#ff6b6b' :
                                    (isLoading && loadingStationId === station.id) ? '#666' : '#4ecdc4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (isLoading && loadingStationId === station.id) ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.3s ease',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {isLoading && loadingStationId === station.id ? 'Connecting...' :
                   (currentStation?.id === station.id && isPlaying) ? 'Playing' : 'Play'}
                </button>

                <button
                  onClick={stopStation}
                  disabled={!isPlaying}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !isPlaying ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  Stop
                </button>
              </div>

              {currentStation?.id === station.id && isPlaying && (
                <div style={{
                  color: '#90ee90',
                  marginTop: '10px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.85rem'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#90ee90',
                    borderRadius: '50%',
                    marginRight: '6px',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}></div>
                  Now Playing: {station.name}
                </div>
              )}
            </div>
          </div>
        ))}
      </main>

      {error && (
        <div style={{
          color: 'red',
          marginTop: '10px',
          padding: '10px',
          backgroundColor: '#ffe6e6',
          borderRadius: '5px',
          maxWidth: '400px',
          margin: '10px auto'
        }}>
          {error}
        </div>
      )}

      {/* Footer Section */}
      <footer style={{
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: 'clamp(0.875rem, 3vw, 1rem)',
        borderTop: '1px solid #374151',
        paddingTop: '2.5rem',
        paddingBottom: '2.5rem',
        marginTop: '4rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <p className="footer-line" style={{ marginBottom: '0.5rem' }}>
          collegerad.io is an independent experiment in sonic&nbsp;culture.
        </p>
        <p className="footer-credit">
          Built with ❤️ + caffeine by{' '}
          <a
            href="mailto:jj@damnjj.wtf"
            style={{
              textDecoration: 'underline',
              color: '#9ca3af',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffffff'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            jj@damnjj.wtf
          </a>
          .
        </p>
        <div className="support-footer">
          <span className="support-copy">
            If this keeps you tuned in, support the signal:{' '}
            <a
              className="support-link"
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {SUPPORT_DISPLAY_URL}
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;
